import SearchIcon from '@mui/icons-material/Search';
import { Grid, Box, Card, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { ICallLogSearch } from 'wisestep-api';
import { SearchBar, WsButton, WsColors, WsFilterDialog, WsDatePicker } from 'wisestep-base';

import ErrorLogger from '../../../providers/ErrorLogger';

export interface CallLogsSearchProps {
  onSearch: (payload: ICallLogSearch) => void;
  defaultValues: ICallLogSearch;
}

const CallLogsSearch = ({ onSearch, defaultValues }: CallLogsSearchProps) => {
  const methods = useForm<ICallLogSearch>({
    defaultValues: defaultValues,
  });
  const { watch, setValue } = methods;

  const formattedDate = (date: string): string => dayjs(date).format('DD/MM/YYYY');
  const [selectedDate, setSelectedDate] = useState<string>();
  const [isDateTime, setDateTime] = useState<boolean>(true);
  const [minDate, setMinDate] = useState<Date>(
    new Date(new Date().setFullYear(new Date().getFullYear() - 1))
  );
  const fromDate = watch('startDate');
  const toDate = watch('endDate');
  const searchQuery = watch('query');
  const [dateFilterStatus] = useState<string>('');

  const clearAllFields = () => {
    defaultValues.endDate = new Date();
    defaultValues.startDate = new Date(new Date().setMonth(new Date().getMonth() - 1));
    defaultValues.serviceId = 'ZOOM';
    defaultValues.query = '';
    onSearch({ ...defaultValues });
  };

  useEffect(() => {
    fromDate &&
      toDate &&
      setSelectedDate(`${formattedDate(`${fromDate}`)} to ${formattedDate(`${toDate}`)}`);
    setMinDate(fromDate);
    dateFilterStatus;
  }, [fromDate, toDate, dateFilterStatus]);

  const handleSearch = () => {
    const payload: ICallLogSearch = {
      serviceId: 'ZOOM',
      type: 'CALL',
      query: searchQuery ?? '',
      startDate: fromDate ?? new Date(new Date().setMonth(new Date().getMonth() - 1)),
      endDate: toDate ?? new Date(),
    };
    setValue('startDate', payload.startDate);
    setValue('endDate', payload.endDate);
    onSearch(payload);
  };

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <Grid item xs={12} pt={3}>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(handleSearch)}>
            <Stack direction="row" spacing={1}>
              {/* search input */}
              <Grid item xs={6} pt={2}>
                <Card sx={{ p: 0.5 }}>
                  <SearchBar
                    name="query"
                    placeholder={'Search for a number or exact recruiter name'}
                  />
                </Card>
              </Grid>

              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <Grid item xs={3} pt={3}>
                  <WsFilterDialog
                    name={selectedDate ?? 'Till Current'}
                    icon={<FaRegCalendarAlt size={21} color={WsColors.TheBlues500} />}
                    label={'Call Logs Between'}
                  >
                    <Box p={1}>
                      <ErrorBoundary
                        FallbackComponent={({ error }) => <ErrorLogger error={error} />}
                      >
                        {isDateTime && (
                          <Stack spacing={2} direction="row" pt={4} pl={1}>
                            <WsDatePicker name="startDate" label="From Date" disableFuture={true} />
                            <WsDatePicker
                              name="endDate"
                              label="To Date"
                              disableFuture={true}
                              minDate={minDate}
                            />
                          </Stack>
                        )}
                      </ErrorBoundary>
                      {!isDateTime && (
                        <Box px={1}>
                          <WsButton variant="text" size="small" onClick={() => setDateTime(true)}>
                            Select Date Range
                          </WsButton>
                        </Box>
                      )}
                      {isDateTime && (
                        <Box px={1}>
                          <WsButton variant="text" size="small" onClick={() => setDateTime(false)}>
                            Select Date Range
                          </WsButton>
                        </Box>
                      )}
                    </Box>
                  </WsFilterDialog>
                </Grid>
              </ErrorBoundary>

              {/* search buttons */}
              <Grid item xs={3} pt={2}>
                <Stack direction={'row'} spacing={1} justifyContent="end">
                  <WsButton
                    className="clear-tags"
                    variant="outlined"
                    size="large"
                    type="reset"
                    onClick={clearAllFields}
                  >
                    Clear
                  </WsButton>
                  <WsButton
                    variant="outlined"
                    size="large"
                    type="submit"
                    startIcon={<SearchIcon />}
                  >
                    Search
                  </WsButton>
                </Stack>
              </Grid>
            </Stack>
          </Box>
        </FormProvider>
      </Grid>
    </ErrorBoundary>
  );
};

export default CallLogsSearch;
